import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ProductService } from '@shared/services/product/product.service';
import { CabRequest, LastBuy } from '@usf/product-types/Customers-Also-Bought';
import {
  debounceTime,
  filter,
  map,
  mergeMap,
  Observable,
  switchMap,
} from 'rxjs';
import { LoadingState, selectCabProduct } from '@usf/ngrx-product';
import { Product } from 'src/app/shared/models/product.model';
import { ProductDetailRelatedProducts } from '@product-detail/models/product-detail-related-products.model';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';

@Injectable({
  providedIn: 'root',
})
export class ProductCabService {
  constructor(
    private productService: ProductService,
    private store: Store,
  ) {}

  getCabProducts(
    product: ProductDetailViewModel,
  ): Observable<ProductDetailRelatedProducts> {
    const cabRequest = this.createCabRequest(product);
    return this.productService.loadCabProductNumbers$(cabRequest).pipe(
      switchMap(_ =>
        this.store.select(selectCabProduct(cabRequest?.productNumber)),
      ),
      filter(
        cabState =>
          cabState?.loadingState === LoadingState.loaded ||
          cabState?.loadingState === LoadingState.error,
      ),
      debounceTime(500),
      mergeMap(cabState =>
        this.productService.loadCABProducts(cabState.products).pipe(
          map(productMap => {
            const products: Product[] = [];
            cabState.products.forEach(rec => {
              const product = productMap.get(rec);
              if (!!product) products.push(product);
            });

            return {
              products,
              variant: cabState?.variant,
            };
          }),
        ),
      ),
    );
  }

  private createCabRequest(product: ProductDetailViewModel): CabRequest {
    const lastBuys: LastBuy[] = [];
    const recentPurchase = product?.recentPurchase;
    for (let i = 1; i < 5; i++) {
      if (!!recentPurchase?.[`purchaseDtm${i}`]) {
        lastBuys.push({
          date: `${new Date(recentPurchase?.[`purchaseDtm${i}`])?.toISOString()?.split('.')[0]}Z`,
          units: recentPurchase?.[`unitsOrdered${i}`]?.toString(),
          eaches: recentPurchase?.[`eachesOrdered${i}`]?.toString(),
          price: recentPurchase?.[`purchasePrice${i}`]?.toString(),
        });
      }
    }

    return {
      productNumber: product?.productNumber,
      pimClassId: product?.summary?.classCode,
      pimGroupId: product?.summary?.groupCode,
      attributes: product?.trackingAttributes,
      lastBuys,
    };
  }
}
